import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import { Card } from '@/components';
import { ExchangeStatusBadge } from '@/components/ExchangeStatusBadge';
import { apiJson, ApiError } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import { formatDateLocal } from '@/lib/utils';
import type { ExchangeLog } from '@/lib/exchange-api';
import type {
  CalendarDay,
  CalendarResponse,
  CheckIn,
  CheckInListResponse,
  CustodyAssignment,
  HouseholdMemberResponse,
  HouseholdResponse,
  ProfessionalClientListResponse,
} from '@/lib/types';
import { SCHEDULE_TYPE_NAMES } from '@/lib/types';

const CUSTODY_COLORS = {
  focalHasKids: '#92CDD5',
  focalHasKidsToday: '#004F5D',
  otherHasKids: '#F1E5D8',
  exception: '#F59E0B',
};

function getMonthRange(date: Date): { startDate: string; endDate: string } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return {
    startDate: formatDateLocal(firstDay),
    endDate: formatDateLocal(lastDay),
  };
}

function getCalendarDays(date: Date): (Date | null)[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay();
  const days: (Date | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) days.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }
  return days;
}

function getActualCustodyHolderStatic(
  assignment: CustodyAssignment,
  focalUserId: number,
  otherParentId: number | undefined
): number {
  if (assignment.exception_status === 'PENDING') {
    if (assignment.assigned_user_id === focalUserId) {
      return otherParentId ?? assignment.assigned_user_id;
    }
    if (otherParentId && assignment.assigned_user_id === otherParentId) {
      return focalUserId;
    }
  }
  return assignment.assigned_user_id;
}

function ProClientCalendarPage() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { getToken } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { startDate, endDate } = useMemo(() => getMonthRange(currentDate), [currentDate]);
  const todayString = formatDateLocal(new Date());

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () => apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
  });
  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );
  const focalUserId = invite?.coparent_user_id;

  const householdQuery = useQuery({
    queryKey: ['professional-client-household', id],
    queryFn: () =>
      apiJson<HouseholdResponse>(getToken, `/v1/professional/clients/${id}/household`),
    enabled: !!invite?.share_custody_calendar,
  });

  const otherMember: HouseholdMemberResponse | undefined = useMemo(() => {
    const members = householdQuery.data?.members ?? [];
    if (!focalUserId) return undefined;
    return members.find((m) => m.user_id !== focalUserId);
  }, [householdQuery.data, focalUserId]);
  const otherParentId = otherMember?.user_id;

  const calendarQuery = useQuery({
    queryKey: ['professional-client-calendar', id, startDate, endDate],
    queryFn: () =>
      apiJson<CalendarResponse>(
        getToken,
        `/v1/professional/clients/${id}/calendar?start_date=${startDate}&end_date=${endDate}`
      ),
    enabled: !!invite?.share_custody_calendar,
  });

  const exchangeQuery = useQuery({
    queryKey: ['professional-client-exchange-logs', id, startDate, endDate],
    queryFn: () => {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
      return apiJson<{ exchange_logs: ExchangeLog[] }>(
        getToken,
        `/v1/professional/clients/${id}/exchange-logs?${qs.toString()}`
      );
    },
    enabled: !!invite?.share_custody_calendar,
  });

  const checkInsQuery = useQuery({
    queryKey: ['professional-client-check-ins', id],
    queryFn: () =>
      apiJson<CheckInListResponse>(getToken, `/v1/professional/clients/${id}/check-ins`),
    enabled: !!invite?.share_custody_calendar,
  });

  const calendarData = calendarQuery.data?.calendar ?? [];
  const calendarDataMap = useMemo(() => {
    const map: Record<
      string,
      {
        assignments: CustodyAssignment[];
        focalHasKids: boolean;
        hasException: boolean;
        hasPendingException: boolean;
      }
    > = {};
    if (!focalUserId) return map;

    calendarData.forEach((day: CalendarDay) => {
      if (day.assignments.length === 0) return;
      const focalHasAny = day.assignments.some((a) => {
        if (a.exception_status === 'PENDING') {
          if (a.assigned_user_id === focalUserId) return false;
          return true;
        }
        return a.assigned_user_id === focalUserId;
      });
      map[day.date] = {
        assignments: day.assignments,
        focalHasKids: focalHasAny,
        hasException: day.assignments.some((a) => a.is_exception),
        hasPendingException: day.assignments.some((a) => a.exception_status === 'PENDING'),
      };
    });
    return map;
  }, [calendarData, focalUserId]);

  const exchangeLogsByKey = useMemo(() => {
    const m = new Map<string, ExchangeLog>();
    for (const log of exchangeQuery.data?.exchange_logs ?? []) {
      m.set(`${log.scheduled_date}|${log.exchange_type}`, log);
    }
    return m;
  }, [exchangeQuery.data]);

  const selectedDayAssignments = useMemo(() => {
    if (!selectedDate) return [];
    return calendarDataMap[selectedDate]?.assignments ?? [];
  }, [selectedDate, calendarDataMap]);

  const uniqueSchedules = useMemo(() => {
    const scheduleMap = new Map<number, { id: number; type: string; beneficiaryName: string }>();
    for (const day of calendarData) {
      for (const a of day.assignments) {
        if (a.schedule_id != null && !scheduleMap.has(a.schedule_id)) {
          scheduleMap.set(a.schedule_id, {
            id: a.schedule_id,
            type: a.schedule_type ?? '',
            beneficiaryName: a.beneficiary_name,
          });
        }
      }
    }
    return [...scheduleMap.values()];
  }, [calendarData]);

  const hasSchedules = uniqueSchedules.length > 0;
  const calendarDays = useMemo(() => getCalendarDays(currentDate), [currentDate]);

  const getParentName = (userId: number): string => {
    if (focalUserId && userId === focalUserId) {
      return invite?.coparent_name?.trim() || 'Inviting co-parent';
    }
    if (otherMember && userId === otherMember.user_id) {
      return (
        [otherMember.first_name, otherMember.last_name].filter(Boolean).join(' ') || 'Co-parent'
      );
    }
    return 'Parent';
  };

  const handleExport = async () => {
    setExportError(null);
    try {
      await downloadProfessionalSectionExport(getToken, id, 'calendar');
    } catch (e: unknown) {
      let msg = 'Could not download export.';
      if (e instanceof ApiError && e.bodyText) {
        try {
          const j = JSON.parse(e.bodyText) as { detail?: string };
          if (j.detail) msg = j.detail;
        } catch {
          /* ignore */
        }
      }
      setExportError(msg);
    }
  };

  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  const recentCheckIns = (checkInsQuery.data?.check_ins ?? []).slice(0, 8);

  return (
    <div style={bodyStyle}>
      <div className='flex items-center justify-between gap-4 mb-4'>
        <h2 className='text-lg font-semibold text-gray-900'>Calendar</h2>
        <button
          type='button'
          onClick={handleExport}
          className='flex items-center gap-2 px-3 py-2 rounded-xl border border-[#018A9D] text-sm font-semibold text-[#018A9D] hover:bg-[#018A9D]/5 shrink-0'
        >
          <FiDownload className='w-4 h-4' />
          Download PDF
        </button>
      </div>
      {exportError ? <p className='text-sm text-red-600 mb-2'>{exportError}</p> : null}

      {hasSchedules ? (
        <div className='flex flex-wrap gap-4 text-sm mb-4'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-4 rounded' style={{ backgroundColor: CUSTODY_COLORS.focalHasKids }} />
            <span className='text-gray-600'>{invite?.coparent_name ?? 'Co-parent'} has kids</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-4 rounded' style={{ backgroundColor: CUSTODY_COLORS.otherHasKids }} />
            <span className='text-gray-600'>Other parent has kids</span>
          </div>
        </div>
      ) : null}

      <Card className='mb-6'>
        <div className='flex items-center justify-between mb-4'>
          <button
            type='button'
            onClick={() => {
              setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
              setSelectedDate(null);
            }}
            className='p-2 hover:bg-gray-100 rounded-full'
          >
            <FiChevronLeft className='w-5 h-5 text-[#018A9D]' />
          </button>
          <h3 className='text-lg font-semibold text-[#018A9D]'>{monthYear}</h3>
          <button
            type='button'
            onClick={() => {
              setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
              setSelectedDate(null);
            }}
            className='p-2 hover:bg-gray-100 rounded-full'
          >
            <FiChevronRight className='w-5 h-5 text-[#018A9D]' />
          </button>
        </div>

        <div className='grid grid-cols-7 gap-1 mb-2'>
          {weekDays.map((day) => (
            <div key={day} className='text-center text-xs font-semibold text-gray-500 py-2'>
              {day}
            </div>
          ))}
        </div>

        <div className='grid grid-cols-7 gap-1'>
          {calendarDays.map((day, index) => {
            if (!day) {
              return <div key={`e-${index}`} className='aspect-square' />;
            }
            const dateStr = formatDateLocal(day);
            const dayData = calendarDataMap[dateStr];
            const isToday = dateStr === todayString;
            const isSelected = dateStr === selectedDate;
            const hasData = !!dayData;

            let bgColor = 'transparent';
            let textColor = 'text-gray-900';
            if (hasData) {
              if (dayData.focalHasKids) {
                bgColor = isToday ? CUSTODY_COLORS.focalHasKidsToday : CUSTODY_COLORS.focalHasKids;
                textColor = isToday ? 'text-white' : 'text-gray-900';
              } else {
                bgColor = CUSTODY_COLORS.otherHasKids;
              }
            }

            return (
              <button
                key={dateStr}
                type='button'
                onClick={() => setSelectedDate(dateStr)}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all hover:ring-2 hover:ring-[#018A9D]/30 ${
                  isSelected ? 'ring-2 ring-gray-900' : ''
                } ${!hasData && isToday ? 'ring-2 ring-[#018A9D]' : ''}`}
                style={{ backgroundColor: bgColor }}
              >
                <span className={`text-sm font-semibold ${textColor}`}>{day.getDate()}</span>
                {dayData?.hasPendingException ? (
                  <div
                    className='absolute bottom-1 w-1.5 h-1.5 rounded-full'
                    style={{ backgroundColor: CUSTODY_COLORS.exception }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </Card>

      {uniqueSchedules.length > 0 ? (
        <div className='mb-6'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
            Schedules
          </p>
          <div className='flex flex-wrap gap-2'>
            {uniqueSchedules.map((s) => (
              <div
                key={s.id}
                className='bg-white rounded-xl border border-gray-200 px-3 py-2 text-sm'
              >
                <span className='font-semibold text-gray-900'>{s.beneficiaryName}</span>
                <span className='text-gray-500'> — {SCHEDULE_TYPE_NAMES[s.type] ?? s.type}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {selectedDate && selectedDayAssignments.length > 0 && focalUserId ? (
        <Card className='mb-6'>
          <h3 className='font-bold text-gray-900 mb-3'>
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </h3>
          <ul className='space-y-3'>
            {selectedDayAssignments.map((a) => {
              const holder = getActualCustodyHolderStatic(a, focalUserId, otherParentId);
              const logKeyPickup = `${selectedDate}|PICKUP`;
              const logKeyDropoff = `${selectedDate}|DROPOFF`;
              const log =
                exchangeLogsByKey.get(logKeyPickup) ?? exchangeLogsByKey.get(logKeyDropoff);

              return (
                <li key={`${a.beneficiary_id}-${a.schedule_id}`} className='text-sm border-b border-gray-100 pb-2'>
                  <p className='font-semibold text-gray-900'>{a.beneficiary_name}</p>
                  <p className='text-gray-600'>
                    Custody: {getParentName(holder)}
                    {a.pickup_time ? ` · Pickup ${a.pickup_time.slice(0, 5)}` : ''}
                    {a.dropoff_time ? ` · Dropoff ${a.dropoff_time.slice(0, 5)}` : ''}
                  </p>
                  {log ? (
                    <div className='mt-1 flex items-center gap-2'>
                      <ExchangeStatusBadge
                        status={log.status}
                        minutesLate={log.minutes_late}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      {recentCheckIns.length > 0 ? (
        <div>
          <h3 className='text-base font-bold text-gray-900 mb-2'>Recent GPS check-ins</h3>
          <ul className='space-y-2 text-sm text-gray-700'>
            {recentCheckIns.map((c: CheckIn) => (
              <li key={c.id} className='bg-white rounded-lg border border-gray-200 px-3 py-2'>
                <span className='text-gray-500'>
                  {new Date(c.created_at).toLocaleString()}
                </span>
                {c.address ? ` — ${c.address}` : null}
                {c.note ? <p className='text-gray-600 mt-1'>{c.note}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {calendarQuery.isLoading ? (
        <p className='text-gray-500 text-center py-4'>Loading calendar…</p>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId/calendar')({
  component: ProClientCalendarPage,
});
